"use client";

import { textFieldStyle } from "@/components/AuthForms/authStyles";
import { useAuth } from "@/contexts/AuthContext";
import { useProfilePassword } from "@/hooks/useProfilePassword";
import { createErrorAdornment, createPasswordFieldStyles } from "@/utils/authUtils";
import { TextField } from "@mui/material";
import React, { useEffect, useState } from "react";

interface MobileProfileFormProps {
  onClose?: () => void;
}

const MobileProfileForm: React.FC<MobileProfileFormProps> = ({ onClose }) => {
  const { user, logout, updateUser } = useAuth();
  const {
    EMPTY_PASSWORD_PLACEHOLDER,
    hasValidNewPassword,
    validatePasswordForSubmission,
    resetPasswordField,
    createNewPasswordVisibilityToggleComponent,
    createCurrentPasswordVisibilityToggleComponent,
    hasPasswordErrors,
    clearPasswordErrors,
    showNewPassword,
    showCurrentPassword,
  } = useProfilePassword();

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    newPassword: "",
    instagramHandle: "",
    currentPassword: "",
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileUpdateError, setProfileUpdateError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        newPassword: EMPTY_PASSWORD_PLACEHOLDER,
        instagramHandle: user.instagram_username || "",
        currentPassword: "",
      });
    }
  }, [user, EMPTY_PASSWORD_PLACEHOLDER]);
  
  // Check if profile data has changed from original user data
  const hasProfileChanges = () => {
    if (!user) return false;

    const hasNewPassword = hasValidNewPassword(profileData.newPassword);

    return (
      profileData.firstName !== (user.first_name || "") ||
      profileData.lastName !== (user.last_name || "") ||
      profileData.instagramHandle !== (user.instagram_username || "") ||
      hasNewPassword
    );
  };

  const handleProfileChange = (field: string, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    setProfileUpdateError(null);
  };

  // Handle password field focus - clear placeholder when user starts typing
  const handlePasswordFocus = () => {
    if (profileData.newPassword === EMPTY_PASSWORD_PLACEHOLDER) {
      setProfileData(prev => ({ ...prev, newPassword: "" }));
      clearPasswordErrors(); // Clear any existing password validation errors
    }
  };

  const handleLogout = () => {
    logout();
    onClose?.();
  };

  const handleSaveProfile = async () => {
    if (!user || !profileData.currentPassword.trim()) {
      setProfileUpdateError("Current password is required to save changes");
      return;
    }

    if (hasValidNewPassword(profileData.newPassword)) {
      if (!validatePasswordForSubmission(profileData.newPassword)) {
        setProfileUpdateError("Please fix password validation errors");
        return;
      }
    }

    setIsUpdatingProfile(true);
    setProfileUpdateError(null);
    clearPasswordErrors();

    try {
      const updateData = {
        email: user.email,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        instagram: profileData.instagramHandle,
        currentPassword: profileData.currentPassword,
        ...(hasValidNewPassword(profileData.newPassword)
          ? { newPassword: profileData.newPassword }
          : {}),
      };

      const response = await updateUser(updateData);

      if (response.success) {
        setProfileData(prev => ({
          ...prev,
          currentPassword: "",
        }));

        resetPasswordField((value) => setProfileData(prev => ({ ...prev, newPassword: value })));

        onClose?.();
      } else {
        setProfileUpdateError(response.error || "Failed to update profile");
      }

    } catch (error) {
      setProfileUpdateError(error instanceof Error ? error.message : "Failed to update profile");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex border-b border-light-grey mb-6 font-raleway text-md">
        <button className="px-2 pb-3 text-center border-b-2 border-black">
          Profile
        </button>
      </div>

      <div className="flex flex-col py-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            value={profileData.firstName}
            onChange={(e) => handleProfileChange("firstName", e.target.value)}
            margin="none"
            sx={textFieldStyle}
          />
          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            value={profileData.lastName}
            onChange={(e) => handleProfileChange("lastName", e.target.value)}
            margin="none"
            sx={textFieldStyle}
          />
        </div>

        <div className="mb-4">
          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            value={user?.email || ""}
            margin="none"
            disabled
            sx={textFieldStyle}
          />
        </div>

        <div className="mb-4">
          <TextField
            label="Change Password"
            variant="outlined"
            fullWidth
            margin="none"
            type="text" // Always use text type for universal browser compatibility
            value={profileData.newPassword}
            placeholder={EMPTY_PASSWORD_PLACEHOLDER}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              handleProfileChange("newPassword", e.target.value)
            }
            onFocus={handlePasswordFocus}
            error={hasPasswordErrors}
            helperText={hasPasswordErrors ? "Please fix password validation errors" : undefined}
            InputProps={{
              endAdornment: createErrorAdornment(
                hasPasswordErrors,
                createNewPasswordVisibilityToggleComponent(profileData.newPassword)
              ),
            }}
            sx={createPasswordFieldStyles(showNewPassword, textFieldStyle)}
          />
        </div>

        <div className="mb-2">
          <TextField
            label="Instagram Handle (Optional)"
            variant="outlined"
            fullWidth
            value={profileData.instagramHandle}
            onChange={(e) => handleProfileChange("instagramHandle", e.target.value)}
            placeholder="Instagram Handle"
            margin="none"
            sx={textFieldStyle}
          />
        </div>
      </div>

      <div className="flex flex-col">
        <p className="text-black text-sm font-raleway mb-4">
          To submit your changes, enter your current password
        </p>
        <div className="mb-4">
          <TextField
            label="Current Password"
            variant="outlined"
            fullWidth
            margin="none"
            type="text" // Always use text type for universal browser compatibility
            value={profileData.currentPassword}
            placeholder="Current Password"
            onChange={(e) => handleProfileChange("currentPassword", e.target.value)}
            InputProps={{
              endAdornment: createErrorAdornment(
                false,
                createCurrentPasswordVisibilityToggleComponent(profileData.currentPassword)
              ),
            }}
            sx={createPasswordFieldStyles(showCurrentPassword, textFieldStyle)}
          />
        </div>

        <button
          onClick={handleSaveProfile}
          disabled={!hasProfileChanges() || !profileData.currentPassword.trim() || isUpdatingProfile || hasPasswordErrors}
          className="w-full py-3 px-3 bg-white text-black border border-black font-raleway text-sm font-bold tracking-wider hover:bg-light-grey disabled:opacity-30 transition-colors"
        >
          {isUpdatingProfile ? "SAVING..." : "SAVE CHANGES"}
        </button>
      </div>

      {profileUpdateError && (
        <div className="text-red text-sm font-raleway mt-2">
          {profileUpdateError}
        </div>
      )}

      <div className="mt-12 mb-12 pb-8">
        <button
          onClick={handleLogout}
          className="w-full py-3 px-3 bg-white text-red border border-red font-raleway text-sm font-bold tracking-wider hover:bg-red-50 transition-colors"
        >
          LOG OUT
        </button>
      </div>
    </div>
  );
};

export default MobileProfileForm;